import { ValuesType } from 'types/generic';

export const RoutePaths = {
    LandingPage: '/:countryCode?',
    Redirect: '/redirect/',
    Verify: '/verify',
    Home: '/home',
    Product: '/product',
    ViewRequests: '/repair',
    Alloy: '/alloy',
    Cosmetic: '/cosmetic',
    Tyre: '/tyre',
    Confirmation: '/confirmation',
    ConfirmationError: '/confirmationerror',
    ToResolveFromProductType: '',
    GeneralError: '/error',
    PersonalDetails: '/personaldetails',
    AmendPersonalDetails: '/amendpersonaldetails',
    AmendAddressDetails: '/amendaddresspage',
    AmendEmailAddress: '/amendEmailAddress',
    AmendBankDetails: '/amendbankdetails',
    AddBankDetails: '/addbankdetails',
    ViewDocuments: '/viewdocuments',
    ClaimDocuments: '/claimdocuments',
    ClaimDetails: '/claimdetailspage',
    ClaimSummary: '/gapclaimsummary',
    ClaimNextSteps: '/claimNextStepsPage',
    ClaimUploadDocuments: '/claimuploaddocuments',
    RepairRequestPreCheckPage: '/repairrequestprecheckpage',
    RepaymentSetup: '/repaymentsetup',
    MissedPayments: '/missedpayments',
    Pay: '/pay'
} as const;

export const PayPaths = {
    Root: RoutePaths.Pay,
    Payment: ':type/:id'
} as const;

export const MissedPaymentsPaths = {
    Root: RoutePaths.MissedPayments,
    MissedPayment: ':policyReference',
    Success: 'success'
} as const;

export const UserInfo = {
    Surname: 'Surname',
    Postcode: 'Postcode'
} as const;

export const ProductTypes = {
    Alloy: 'Alloy Wheel Insurance',
    AlloyRepair: 'Alloy Wheel Repair',
    Cosmetic: 'Cosmetic Insurance',
    CosmeticRepair: 'Cosmetic Repair',
    JointAlloyCosmetic: 'Joint Alloy and Cosmetic',
    Warranty: 'Warranty',
    GAP: 'GAP Insurance',
    JointAlloyTyre: 'Joint Alloy and Tyre',
    Tyre: 'Tyre'
} as const;

export const ProductStatuses = {
    Active: 'Active'
} as const;

export const ClaimStatuses = {
    Authorised: 'Authorised',
    BulkRejected: 'Bulk Rejected',
    Closed: 'Closed',
    CorrectionsNeeded: 'Corrections Needed',
    Notified: 'Notified',
    Paid: 'Paid',
    Referred: 'Referred',
    Rejected: 'Rejected',
    Transferred: 'Transferred',
    Canceled: 'Canceled',
    NotComplete: 'Not Complete',
    Withdrawn: 'Withdrawn',
    WithdrawnAuto: 'Withdrawn (Auto)'
} as const;

export const ActiveClaimStatuses: (typeof ClaimStatuses)[keyof typeof ClaimStatuses][] =
    [
        ClaimStatuses.Authorised,
        ClaimStatuses.CorrectionsNeeded,
        ClaimStatuses.Notified,
        ClaimStatuses.Referred
    ];

export const UploadImageTypes = {
    CloseUp: 'CloseUp',
    Distance: 'Distance',
    AdditionalImage1: 'AdditionalImage1',
    AdditionalImage2: 'AdditionalImage2',
    Mileage: 'Mileage',
    Registration: 'Registration'
} as const;

export const PageNames = {
    Landing: 'Landing',
    Vehicle: 'Vehicle',
    Product: 'Product',
    PriorRepairRequest: 'PriorRepairRequest',
    AlloyGeneral: 'AlloyGeneral',
    AlloyWheelSelection: 'AlloyWheelSelection',
    AlloySummary: 'AlloySummary',
    AlloyConfirmation: 'AlloyConfirmation',
    CosmeticRepairRequests: 'CosmeticRepairRequests',
    CosmeticRepairerAddress: 'CosmeticRepairerAddress',
    CosmeticSummary: 'CosmeticSummary',
    CosmeticConfirmation: 'CosmeticConfirmation',
    TyreSelection: 'TyreSelection',
    TyreSummary: 'TyreSummary'
} as const;

export const DowntimeMessageType = {
    Warning: 'Warning',
    Downtime: 'Downtime'
} as const;

export const EmailUpdateStatus = {
    Failed: 0,
    UpdatedInVsc: 1,
    UpdatedInAuth0: 2
} as const;

export const Countries = {
    Uk: 'GB',
    Us: 'US',
    De: 'DE',
    At: 'AT',
    Es: 'ES'
} as const;

export const Languages = {
    Uk: 'en',
    Us: 'us',
    De: 'de',
    At: 'at',
    Es: 'es'
} as const;

export const LocaleLanguageCode = {
    DE: 'de-DE',
    AT: 'de-AT',
    GB: 'en-GB',
    US: 'en-US',
    ES: 'es-ES'
};

export const AccountType = {
    Collections: 'Collections',
    Disbursement: 'Disbursement'
};

export const Provinces = {
    optionsdata: [
        { key: 'AB', value: 'Alberta' },
        { key: 'BC', value: 'British Columbia' },
        { key: 'MB', value: 'Manitoba' },
        { key: 'NB', value: 'New Brunswick' },
        { key: 'NL', value: 'Newfoundland and Labrador' },
        { key: 'NS', value: 'Nova Scotia' },
        { key: 'ON', value: 'Ontario' },
        { key: 'PE', value: 'Prince Edward Island' },
        { key: 'QC', value: 'Quebec' },
        { key: 'SK', value: 'Saskatchewan' },
        { key: 'NT', value: 'Northwest Territories' },
        { key: 'NU', value: 'Nunavut' },
        { key: 'YT', value: 'Yukon' }
    ]
} as const;

export const states = {
    optionsdata: [
        { key: 'AL', value: 'Alabama' },
        { key: 'AK', value: 'Alaska' },
        { key: 'AZ', value: 'Arizona' },
        { key: 'AR', value: 'Arkansas' },
        { key: 'CA', value: 'California' },
        { key: 'CO', value: 'Colorado' },
        { key: 'CT', value: 'Connecticut' },
        { key: 'DE', value: 'Delaware' },
        { key: 'FL', value: 'Florida' },
        { key: 'GA', value: 'Georgia' },
        { key: 'HI', value: 'Hawaii' },
        { key: 'ID', value: 'Idaho' },
        { key: 'IL', value: 'Illinois' },
        { key: 'In', value: 'Indiana' },
        { key: 'IA', value: 'Iowa' },
        { key: 'KS', value: 'Kansas' },
        { key: 'KY', value: 'Kentucky' },
        { key: 'LA', value: 'Louisiana' },
        { key: 'ME', value: 'Maine' },
        { key: 'MD', value: 'Maryland' },
        { key: 'MA', value: 'Massachusetts' },
        { key: 'MI', value: 'Michigan' },
        { key: 'MN', value: 'Minnesota' },
        { key: 'MS', value: 'Mississippi' },
        { key: 'MO', value: 'Missouri' },
        { key: 'MT', value: 'Montana' },
        { key: 'NE', value: 'Nebraska' },
        { key: 'NV', value: 'Nevada' },
        { key: 'NH', value: 'New Hampshire' },
        { key: 'NJ', value: 'New Jersey' },
        { key: 'NM', value: 'New Mexico' },
        { key: 'NY', value: 'New York' },
        { key: 'NC', value: 'North Carolina' },
        { key: 'ND', value: 'North Dakota' },
        { key: 'OH', value: 'Ohio' },
        { key: 'OK', value: 'Oklahoma' },
        { key: 'OR', value: 'Oregon' },
        { key: 'PA', value: 'Pennsylvania' },
        { key: 'RI', value: 'Rhode Island' },
        { key: 'SC', value: 'South Carolina' },
        { key: 'SD', value: 'South Dakota' },
        { key: 'TN', value: 'Tennessee' },
        { key: 'TX', value: 'Texas' },
        { key: 'UT', value: 'Utah' },
        { key: 'VT', value: 'Vermont' },
        { key: 'VA', value: 'Virginia' },
        { key: 'WA', value: 'Washington' },
        { key: 'WV', value: 'West Virginia' },
        { key: 'WI', value: 'Wisconsin' },
        { key: 'WY', value: 'Wyoming' }
    ]
} as const;

export const SelectOptionsYesNo = {
    Yes: 'Yes',
    No: 'No'
} as const;
export type YesNo = ValuesType<typeof SelectOptionsYesNo>;

export const InsuranceDocumentsList = [
    {
        documentName: 'Primary insurance settlement statement',
        helpText:
            'Complete copy of your insurance settlement statement including the date of and cause of the Total Loss of the Covered Vehicle, gross settlement amount, deductible and net settlement amount.',
        requiredIfNoInsurance: false,
        additionalDocument: false
    },
    {
        documentName: 'Total evaluation report',
        helpText:
            'Complete copy showing the basis of how the Actual Cash Value of the Covered Vehicle was determined (must include all options on the Covered Vehicle and mileage on the Date of Loss, if available).',
        requiredIfNoInsurance: false,
        additionalDocument: false
    },
    {
        documentName: 'Insurer settlement check',
        helpText: 'Copy of the primary insurer’s settlement check',
        requiredIfNoInsurance: false,
        additionalDocument: false
    },
    {
        documentName: 'Loan payoff history',
        helpText:
            'Copy of a statement from Assignee/lender documenting the loan pay-out amount, including detail of past due amounts, late charges, etc., if any.',
        requiredIfNoInsurance: true,
        additionalDocument: false
    },
    {
        documentName: 'Complete Payment history',
        helpText:
            'Copy of complete pay-out history (record of loan payments from inception through Date of Loss).',
        requiredIfNoInsurance: true,
        additionalDocument: false
    },
    {
        documentName: 'Finance Agreement',
        helpText:
            'Copy of your loan finance agreement including term, APR, amount financed, payment amount, payment due date and any cancellable items purchased.',
        requiredIfNoInsurance: true,
        additionalDocument: false
    },
    {
        documentName: 'Vehicle purchase order',
        helpText:
            'Documentation from the Dealer substantiating make, model, year, mileage and vehicle options.',
        requiredIfNoInsurance: true,
        additionalDocument: false
    },
    {
        documentName: 'GAP/Waiver contract addendum',
        helpText: 'Copy of a purchased GAP Waiver Contract Addendum/Coverage',
        requiredIfNoInsurance: true,
        additionalDocument: false
    },
    {
        documentName: 'Police report',
        helpText:
            'Copy of the police report if a Police Report was filed. Please note, a Police Report is required if the Total Loss was a result of the theft of the Covered Vehicle.',
        requiredIfNoInsurance: true,
        additionalDocument: false
    },
    {
        documentName: 'Refund documentation for cancellable items',
        helpText:
            'Copies of the documentation showing refund amounts of any cancellable items purchased. Example: Pre-paid maintenance plans, vehicle service contracts, vehicle upgrades.',
        requiredIfNoInsurance: true,
        additionalDocument: false
    },
    {
        documentName: 'Other documents',
        helpText:
            'Free form field for the customer to self-categorize the document they’re attaching..',
        requiredIfNoInsurance: true,
        additionalDocument: true
    }
];

export const TyreRepairOptions = {
    Dealer: 'Your selling dealership',
    KwikFit: 'Kwik Fit/ Kwik Fit Mobile',
    PreferredGarage: 'Preferred Garage'
} as const;

export const SupportedLanguages = [
    LocaleLanguageCode.US,
    LocaleLanguageCode.GB,
    LocaleLanguageCode.DE,
    LocaleLanguageCode.ES
];

export const PaymentEmailIdsMap = {
    [Countries.Uk]: 'policyholderbusiness@carcareplan.co.uk',
    [Countries.Us]: 'policyholderbusiness@carcareplan.co.uk'
};
